/**
 * 66.8, digging in — the dug ground's look by material (lane E; decision 160, the owner on #956: "Design looks good for
 * digging in"; 63.5 step 5's family, as a terrain edit's look reads it). Two digs, each held by one person facing +z (the
 * enemy): a **scrape** (a body-long hollow for a prone holder, its spoil a berm in front) and a **foxhole** (a pit dug to a
 * kneeling depth, a parapet in front and at its sides). A hole is dug until the holder's head is at the lip, whatever the
 * ground: the material sets how it looks (and, for lane C's entrench order, how long it takes), not how much it covers —
 *   - **soil**: firm walls, the dark spoil lip, clods thrown on it;
 *   - **sand**: a soft bowl — the walls slumped wide, the lip low and spread (so dug deeper for the same cover);
 *   - **snow**: cut walls — steep and clean, a packed grey-blue floor, a bright cut band at the rim;
 *   - **mud**: a wet edge — a dark band at the rim, water seeping into the floor, splashes on the lip;
 *   - **ash** soft like sand, grey; **salt** a crust broken into a low pale lip;
 *   - **rock** and **ice**: refused — nothing is dug (null).
 * Presentation only: `digShape` gives a dig's heights for the design sheets now and for a dig's terrain edit to be drawn by
 * later (lane C's entrench order and edit log come after bound-with-cover); `digCover` gives the two numbers a holder's
 * pressed pose takes (the cover's height over the floor and the dug face's gap). Person units (a person is 1.8).
 */
import type { GroundMaterial } from '../types';
import { DIG_GROUND, digGeometry, type DigKind } from '../core/dig';
import { AFTER_HIT } from '../constants/groundLook';

export interface DigLook {
  /** how soft the walls are (person units the wall leans back over the depth): a clean cut 0.04 … a slump 0.5 */
  wall: number;
  /** × the dig's lip height, × its width (the depth makes up the rest of the cover) */
  lip: number; lipWide: number;
  /** the floor, the walls, the spoil, and a band at the rim if it has one (snow's cut, mud's wet edge) */
  floor: string; walls: string; spoil: string; edge?: string;
  /** water seeping into the floor (mud) */
  water?: string;
}
export const DIG_LOOK: Record<GroundMaterial, DigLook | null> = {
  soil: { ...DIG_GROUND.soil!, floor: AFTER_HIT.soil.core, walls: '#5a4630', spoil: '#4a3a28' },
  sand: { ...DIG_GROUND.sand!, floor: AFTER_HIT.sand.core, walls: '#b49a6e', spoil: '#bca67c' },
  snow: { ...DIG_GROUND.snow!, floor: '#8a96a2', walls: '#c4d0dc', spoil: '#f2f6fa', edge: '#ffffff' },
  mud: { ...DIG_GROUND.mud!, floor: AFTER_HIT.mud.core, walls: '#3a2e22', spoil: '#3e3024', edge: '#1c1610', water: '#2a2620' },
  ash: { ...DIG_GROUND.ash!, floor: '#6e6a66', walls: '#8a8580', spoil: AFTER_HIT.ash.ring },
  salt: { ...DIG_GROUND.salt!, floor: AFTER_HIT.salt.core, walls: '#d8d2c4', spoil: AFTER_HIT.salt.ring },
  rock: null,
  ice: null,
  scree: null,
};
/** The ground before it is dug, by material (the sheets' undisturbed ground; soil grassed). */
export const UNDUG_GROUND: Record<GroundMaterial, string> = { soil: '#5d7a46', rock: AFTER_HIT.rock.ground, sand: AFTER_HIT.sand.ground, snow: AFTER_HIT.snow.ground, ice: AFTER_HIT.ice.ground, mud: AFTER_HIT.mud.ground, ash: AFTER_HIT.ash.ground, salt: AFTER_HIT.salt.ground, scree: AFTER_HIT.scree.ground };

/** 66.8: the shape is the sim's (core/dig.ts, ARCHITECTURE §5.18) — moved there so the fine ground and the cover map read the
 *  same heights; the look adds its colours. */
export { DIGS, digCover, type DigKind } from '../core/dig';

export interface DigShape {
  look: DigLook;
  /** the floor's depth under the ground and the lip's top over it (`depth + top` is the cover) */
  depth: number; top: number;
  /** the floor's share of the depth at (x, z), 0 … 1 */
  pit: (x: number, z: number) => number;
  /** the spoil's height over the ground at (x, z) */
  spoil: (x: number, z: number) => number;
  /** the ground's height at (x, z): −depth · pit + spoil */
  height: (x: number, z: number) => number;
}
/**
 * A dig of `kind` in `material`, `done` of the way dug (0 … 1: the hole deepens and its spoil grows as it is dug). Null
 * where the ground refuses a dig (rock, ice). Pure: the sim's geometry (core/dig.ts digGeometry) with this ground's look.
 */
export function digShape(kind: DigKind, material: GroundMaterial, done = 1): DigShape | null {
  const g = digGeometry(kind, material, done), look = DIG_LOOK[material];
  if (!g || !look) return null;
  return { look, depth: g.depth, top: g.top, pit: g.pit, spoil: g.spoil, height: g.height };
}
